// Round avatar/image surface with a graceful fallback to initials when
// `src` is missing or fails to load.
//
// TODO(witems-promote): Generic enough to live in @platform/witems —
// any avatar / icon-bubble / user-shortcode-tile surface across Withings
// would use the same shape. Keep the API narrow (src/alt/fallback/size/
// className) so the migration is one-import swap when witems ships the
// equivalent, or when we contribute it upstream. See .claude/rules/
// withings-conventions.md "When witems doesn't ship the primitive".

import { useEffect, useState } from 'react';

interface MediaProps {
  src?: string | null;
  alt?: string;
  /** Text shown when no image is available (typically 1–3 chars). */
  fallback?: string;
  /** Square edge in px. Default 36 (sidebar avatar size). */
  size?: number;
  className?: string;
}

export default function Media({
  src, alt = '', fallback, size = 36, className,
}: MediaProps) {
  const [failed, setFailed] = useState(false);

  // Reset the failure flag whenever `src` changes — otherwise a transient
  // network error on one URL would lock the component on fallback even
  // after the caller swaps in a working URL.
  useEffect(() => { setFailed(false); }, [src]);

  const showImage = !!src && !failed;

  return (
    <div
      className={['media', className].filter(Boolean).join(' ')}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(11, Math.round(size * 0.38)),
      }}
    >
      {showImage ? (
        <img src={src as string} alt={alt} onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{fallback ?? '?'}</span>
      )}
    </div>
  );
}
